import axios from "axios";

// 全局設定 AJAX Request 攔截器 (interceptor)
axios.interceptors.request.use(
  async function (config) {
    const accessToken = localStorage.getItem("accessToken");

    // 發出 API request 時 headers Authorization 自動帶上 accessToken
    config.headers.Authorization = `Bearer ${accessToken}`;

    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

// ==================== Refresh Token 防並發機制 ====================
let isRefreshing = false;
let pendingRequests = [];

const processQueue = (error, token = null) => {
  pendingRequests.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  pendingRequests = [];
};

// 全局設定 AJAX Response 攔截器 (interceptor)
axios.interceptors.response.use(
  function (response) {
    return response;
  },
  function (error) {
    if (error.response) {
      switch (error.response.status) {
        case 400: {
          const { message } = error.response.data;
          alert(`${error.response.status}: ${message || "資料錯誤"}。`);
          break;
        }

        case 401: {
          const refreshTokenUrl = `${import.meta.env.VITE_PRIEMS_API_URL}/v1/auth/refresh`;

          // 如果是 refresh 請求本身失敗，直接跳過
          if (error.config.url === refreshTokenUrl) {
            return Promise.reject(error);
          }

          const originalRequest = error.config;

          // 如果正在 refresh，將請求排入佇列等待
          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              pendingRequests.push({ resolve, reject });
            }).then((token) => {
              originalRequest.headers.Authorization = "Bearer " + token;
              return axios(originalRequest);
            });
          }

          // 第一個 401：開始 refresh
          isRefreshing = true;
          const refreshToken = localStorage.getItem("refreshToken");

          // ⚠️ 2026-09-09 修正：Priems 的 /v1/auth/refresh 規格為
          //    security: HTTPBearer、requestBody: null —— 它不讀 body，
          //    必須把 refreshToken 放在 Authorization header。
          //    原寫法為 body 帶 {refreshToken, expiresInMins}（dummyjson 範本殘留），
          //    實測固定回 401 → refresh 必定失敗 → 使用者被登出。
          //    access token 僅 15 分鐘，這就是客戶回報「常常自動登出」的根因。
          return axios
            .post(refreshTokenUrl, null, {
              headers: { Authorization: `Bearer ${refreshToken}` },
            })
            .then((response) => {
              const newToken = response.data.accessToken;

              // 更新 localStorage
              localStorage.setItem("accessToken", newToken);

              // 如果 API 有回傳新的 refreshToken 也一起更新（支援多裝置共存）
              if (response.data.refreshToken) {
                localStorage.setItem("refreshToken", response.data.refreshToken);
              }

              // 重送原始 request
              originalRequest.headers.Authorization = "Bearer " + newToken;

              // 通知所有等待中的請求
              processQueue(null, newToken);

              return axios(originalRequest);
            })
            .catch((err) => {
              // refresh 失敗，通知所有等待中的請求
              processQueue(err);

              localStorage.clear();
              alert("作業逾時或無相關使用授權，請重新登入");
              window.location.href = "/login";
              return Promise.reject(error);
            })
            .finally(() => {
              isRefreshing = false;
            });
        }

        case 404:
          alert(`${error.response.status}: 資料來源不存在`);
          break;

        case 500:
          alert(`${error.response.status}: 內部系統發生錯誤`);
          break;

        default:
          alert(
            `${error.response.status}: 系統維護中，造成您的不便，敬請見諒。`
          );
          break;
      }
    } else {
      if (
        error.code === "ECONNABORTED" &&
        error.message &&
        error.message.indexOf("timeout") !== -1
      ) {
        alert("網路連線逾時，請點「確認」鍵後繼續使用。");
      } else {
        alert("網路連線不穩定，請稍候再試");
      }
    }

    return Promise.reject(error);
  }
);

export default axios;
